import { Autocomplete, Button, Grid, Menu, MenuItem, Skeleton, TextField } from '@mui/material'
import { SubmissionsLogsFilters as Filters, MenuState, SelectedSubmission } from './types'
import { AutoCompleteOptions, LookupTables } from '../../../../hooks/useLookupTables'
import { HighlightOff, MoreHoriz } from '@mui/icons-material'
import MenuOptions from '../../../assets/MenuOptions'

type Props = {
	filters: Filters
	setFilters: React.Dispatch<React.SetStateAction<Filters>>
	lookupTables: Pick<LookupTables, "affiliations" | 'terms'> | null
	isLoadingLookupTables: boolean
	errorLookupTables: boolean
	sections: AutoCompleteOptions[] | null
	errorSections: boolean
	isLoadingSections: boolean,
	selectedRows: Map<number, SelectedSubmission>,
	actionsMenu: MenuState,
	setActionsMenu: React.Dispatch<React.SetStateAction<MenuState>>,
	handleCancelSubmissions: () => void
}

export default function SubmissionsLogsFilters({
	lookupTables,
	isLoadingLookupTables,
	errorLookupTables,
	filters,
	setFilters,
	sections,
	isLoadingSections,
	errorSections,
	selectedRows,
	actionsMenu,
	setActionsMenu,
	handleCancelSubmissions
}: Props) {

	if (!lookupTables || isLoadingLookupTables || errorLookupTables || !sections || isLoadingSections || errorSections) {
		return <Skeleton variant="rectangular" width={'100%'} height={'5vh'} />
	}

	return (
		<>
			<Grid item xs={1.25}>
				<Autocomplete
					id='filter-term'
					options={lookupTables.terms}
					value={filters.term}
					onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, term: newValue }))}
					renderInput={props => <TextField {...props} label='Term' />}
					disableClearable
					isOptionEqualToValue={(option, value) => option.id === value.id}
				/>
			</Grid>
			<Grid item xs={2.25}>
				<Autocomplete
					id='filter-affiliation'
					options={lookupTables?.affiliations.map((aff) => ({ id: aff.id, label: aff.label }))}
					value={filters.affiliation}
					onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, affiliation: newValue }))}
					renderInput={props => <TextField {...props} label='Affiliation' />}
					isOptionEqualToValue={(option, value) => option.id === value.id}
				/>
			</Grid>
			<Grid item xs={2.5}>
				<Autocomplete
					id='filter-section'
					options={sections}
					value={filters.section}
					onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, section: newValue }))}
					renderInput={props => <TextField {...props} label='Section' />}
					isOptionEqualToValue={(option, value) => option.id === value.id}
				/>
			</Grid>
			<Grid item xs={2.25}>
				<TextField
					id='filter-student-name'
					label="Student name"
					value={filters.name}
					onChange={(e) =>
						setFilters((prev) => ({
							...prev,
							name: e.target.value
						}))
					}
				/>
			</Grid>
			<Grid item xs={1}>
				<TextField
					select
					label="Page Blur"
					value={filters.page_blurred}
					onChange={(e) => setFilters((_filters) => ({ ..._filters, page_blurred: Number(e.target.value) }))}
					fullWidth
				>
					<MenuItem value={0}>All</MenuItem>
					<MenuItem value={1}>Only Blurred</MenuItem>
					<MenuItem value={2}>Greater than 2</MenuItem>
				</TextField>
			</Grid>
			<Grid item xs={1}>
				<TextField
					select
					label="Score"
					value={filters.max_score}
					onChange={(e) => setFilters((_filters) => ({ ..._filters, max_score: Number(e.target.value) }))}
					fullWidth
				>
					<MenuItem value={0}>All</MenuItem>
					<MenuItem value={1}>Has max score</MenuItem>
				</TextField>
			</Grid>
			<Grid item xs={1}>
				<TextField
					select
					label="Auditable"
					value={filters.auditable}
					onChange={(e) => setFilters((_filters) => ({ ..._filters, auditable: Number(e.target.value) }))}
					fullWidth
				>
					<MenuItem value={0}>All</MenuItem>
					<MenuItem value={1}>Only Auditable</MenuItem>
				</TextField>
			</Grid>
			<Grid item xs={0.75}>
				<Button
					id='button-actions-menu'
					onClick={(e) => setActionsMenu({ anchorEl: e.currentTarget })}
				>
					<MoreHoriz />
				</Button>
			</Grid>

			<Menu
				open={Boolean(actionsMenu.anchorEl)}
				anchorEl={actionsMenu.anchorEl}
				onClose={() => setActionsMenu({ anchorEl: null })}
				onClick={() => setActionsMenu({ anchorEl: null })}
			>
				<MenuOptions
					options={[
						{
							label: 'Cancel selected submission(s)',
							icon: <HighlightOff color='warning' />,
							disabled: selectedRows.size === 0,
							onClick: handleCancelSubmissions
						}
					]}
				/>
			</Menu>
		</>
	);
}
